/**
 * Node approval, join keys and permissions: the platform's side of "a node
 * attaches to AIOE and is live only after an approved user signs off"
 * (mju-ai-workbench docs/plans/2026-09-agent-nodes.md, "Joining"). AIOE owns
 * these shapes; the workbench's `mojoup-node` only needs the join call and
 * the token errors it already knows (`authorization_pending`, `access_denied`).
 *
 *   GET  /admin/v1/nodes                         nodes (kind node) with approval and surface diff
 *   GET  /admin/v1/nodes/pending                 node grants waiting for an approver
 *   POST /admin/v1/nodes/pending/:userCode/approve  { tags?, surface?, owner? }
 *   POST /admin/v1/nodes/pending/:userCode/refuse
 *   POST /admin/v1/nodes/:deviceId/surface/approve  { surface? }   a grown surface
 *   POST /admin/v1/nodes/:deviceId/surface/refuse
 *   PATCH /admin/v1/nodes/:deviceId              { tags?, owner? }
 *   GET|POST /admin/v1/nodes/join-keys, DELETE /admin/v1/nodes/join-keys/:id
 *   POST /relay/v1/device/join                   { key, manifest, name, tags? } -> token pair
 *   GET|PUT /admin/v1/roles                      extra permissions per role
 *   GET  /admin/v1/audit                         platform audit (admin actions)
 */
import { z } from "zod";
import { nodeManifestSchema, nodeRoleSchema } from "./nodeManifest.js";
/** Permissions a role can hold beyond its built-in reach. `admin` holds every one. */
export const PERMISSIONS = ["nodes:approve", "portfolio:manage", "memory:write"];
export const permissionSchema = z.enum(PERMISSIONS);
/** `PUT /admin/v1/roles`: which roles hold which permissions (admin is implied and cannot lose any). */
export const rolePermissionsSchema = z.record(z.string().min(1).max(64), z.array(permissionSchema).max(PERMISSIONS.length));
const tagSchema = z.string().min(1).max(64);
/**
 * What an approver lets a node offer, chosen from its manifest. Every list is
 * of ids in the manifest; an absent list keeps everything the manifest offers.
 */
export const surfaceSelectionSchema = z.object({
    roles: z.array(nodeRoleSchema).optional(),
    runtimes: z.array(z.string()).optional(),
    models: z.array(z.string()).optional(),
    /** Memory by kind (`graphify`, `obsidian`), not by entry: a graph follows a repository the surface already lists, so a new root's graph is not new surface. */
    memory: z.array(z.string()).optional(),
    repos: z.array(z.string()).optional(),
    services: z.array(z.string()).optional(),
    sandbox: z.boolean().optional(),
    computer: z.boolean().optional(),
    /** Agents run in a sandbox on this node (its start-up check said it can). */
    canSandbox: z.boolean().optional(),
});
export const nodeOwnerChoiceSchema = z.object({ type: z.enum(["org", "project"]), id: z.string().min(1).max(200).optional() });
export const nodeApproveSchema = z.object({
    tags: z.array(tagSchema).max(32).optional(),
    surface: surfaceSelectionSchema.optional(),
    /** `project` assigns the node to one project at approval; default the organisation. */
    owner: nodeOwnerChoiceSchema.optional(),
});
export const nodePatchSchema = z.object({
    tags: z.array(tagSchema).max(32).optional(),
    owner: nodeOwnerChoiceSchema.optional(),
});
export const joinKeyCreateSchema = z.object({
    name: z.string().min(1).max(200),
    tags: z.array(tagSchema).max(32).default([]),
    /** Hours until the key stops working; at most 90 days. */
    expiresInHours: z.number().int().min(1).max(24 * 90).default(24),
    reusable: z.boolean().default(false),
    ephemeral: z.boolean().default(false),
    /** The surface pre-approved for nodes using the key; a node offering more waits for approval. */
    surface: nodeManifestSchema.partial().optional(),
    projectId: z.string().min(1).max(200).optional(),
    /** D447: join the node to one of the organisation's approved clusters and one of its node pools; the key's tags are then the pool's. */
    clusterId: z.string().min(1).max(100).optional(),
    nodePool: z.string().min(1).max(63).optional(),
});
export const nodeJoinSchema = z.object({
    key: z.string().min(10).max(200),
    manifest: nodeManifestSchema,
    name: z.string().min(1).max(200),
    tags: z.array(tagSchema).max(32).optional(),
});
/** What the console shows for a node waiting to join. */
export const pendingNodeSchema = z.object({
    userCode: z.string(),
    name: z.string(),
    requestedDeviceId: z.string().optional(),
    tags: z.array(z.string()),
    manifest: nodeManifestSchema.optional(),
    claimedBy: z.string().optional(),
    claimedEmail: z.string().optional(),
    createdAt: z.string(),
    expiresAt: z.string(),
});
export function surfaceItems(m) {
    return {
        roles: [...(m?.roles ?? [])],
        runtimes: (m?.runtimes ?? []).map((r) => r.id),
        models: (m?.models ?? []).map((r) => r.id),
        memory: [...new Set((m?.memory ?? []).map((r) => r.kind))],
        repos: (m?.repos ?? []).map((r) => r.rootId),
        services: Object.entries(m?.services ?? {}).filter(([, v]) => v !== undefined).map(([k]) => k),
        sandbox: Boolean(m?.sandbox),
        computer: Boolean(m?.computer),
        canSandbox: Boolean(m?.sandboxCheck?.canSandbox),
    };
}
const LIST_KEYS = ["roles", "runtimes", "models", "memory", "repos", "services"];
/** What `next` offers that `approved` did not, and the reverse. Resources and versions are not surface. */
export function surfaceDiff(approved, next) {
    const a = surfaceItems(approved);
    const b = surfaceItems(next);
    const added = surfaceItems(undefined);
    const removed = surfaceItems(undefined);
    for (const k of LIST_KEYS) {
        added[k] = b[k].filter((x) => !a[k].includes(x));
        removed[k] = a[k].filter((x) => !b[k].includes(x));
    }
    added.sandbox = b.sandbox && !a.sandbox;
    added.computer = b.computer && !a.computer;
    removed.sandbox = a.sandbox && !b.sandbox;
    removed.computer = a.computer && !b.computer;
    added.canSandbox = b.canSandbox && !a.canSandbox;
    removed.canSandbox = a.canSandbox && !b.canSandbox;
    const grew = LIST_KEYS.some((k) => added[k].length > 0) || added.sandbox || added.computer || added.canSandbox;
    return { added, removed, grew };
}
/** The manifest cut down to what a selection allows. */
export function trimSurface(m, sel) {
    if (!sel)
        return m;
    const keep = (items, ids, id) => (ids ? items.filter((t) => ids.includes(id(t))) : items);
    const services = sel.services ? Object.fromEntries(Object.entries(m.services).filter(([k]) => sel.services.includes(k))) : m.services;
    return {
        ...m,
        roles: sel.roles ? m.roles.filter((r) => sel.roles.includes(r)) : m.roles,
        runtimes: keep(m.runtimes, sel.runtimes, (r) => r.id),
        models: keep(m.models, sel.models, (r) => r.id),
        memory: keep(m.memory, sel.memory, (r) => r.kind),
        repos: keep(m.repos, sel.repos, (r) => r.rootId),
        services,
        sandbox: sel.sandbox === false ? undefined : m.sandbox,
        computer: sel.computer === false ? undefined : m.computer,
        // Not approved to sandbox: the check is kept for the console, but it does not say it can.
        ...(sel.canSandbox === false && m.sandboxCheck ? { sandboxCheck: { ...m.sandboxCheck, canSandbox: false } } : {}),
    };
}
/** The surface both allow: what an approval stays at when the node offers less than before. */
export function intersectSurface(allowed, offered) {
    const a = surfaceItems(allowed);
    return trimSurface(offered, { roles: a.roles, runtimes: a.runtimes, models: a.models, memory: a.memory, repos: a.repos, services: a.services, sandbox: a.sandbox, computer: a.computer, canSandbox: a.canSandbox });
}
//# sourceMappingURL=nodes.js.map